import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react"
import { BrowserTypes, DeviceTypes, OSTypes } from "@prismancy/shared"
import { hasBrowserEventListeners } from "../utils/browserEvent"

export type BrowserTabInfo = {
  tabId: number | undefined | null
  tabUrl: string | undefined | null
  windowId: number | undefined | null
  isSupportedPage: boolean
  pageRevision: number
}

type BrowserContextType = {
  tabInfo: BrowserTabInfo
  browser: BrowserTypes | string | null
  os: OSTypes | string | null
  device: DeviceTypes | string | null
}

type BrowserProviderProps = {
  children: ReactNode
}

const BrowserContext = createContext<BrowserContextType | null>(null)

const EMPTY_TAB_INFO: BrowserTabInfo = {
  tabId: null,
  tabUrl: null,
  windowId: null,
  isSupportedPage: false,
  pageRevision: 0,
}

const LIVE_EDITOR_PORT_NAME = "prismancy-live-editor"

const isSupportedPageUrl = (url: string | undefined): boolean => {
  if (!url) return false

  try {
    const protocol = new URL(url).protocol
    return protocol === "http:" || protocol === "https:"
  } catch {
    return false
  }
}

export const BrowserProvider: React.FC<BrowserProviderProps> = ({ children }) => {

  const [tabInfo, setTabInfo] = useState<BrowserTabInfo>(EMPTY_TAB_INFO)
  const [browser, setBrowser] = useState<BrowserTypes | string | null>(null)
  const [os, setOs] = useState<OSTypes | string | null>(null)
  const [device, setDevice] = useState<DeviceTypes | string | null>(null)
  const ownerWindowIdRef = useRef<number | null>(null)
  const activeTabIdRef = useRef<number | null>(null)
  const refreshSequenceRef = useRef(0)

  const getBrowser = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Chrome") && userAgent.includes("Edg")) {
      setBrowser(BrowserTypes.Edge);
    } else if (userAgent.includes("Chrome")) {
      setBrowser(BrowserTypes.Chrome)
    } else if (userAgent.includes("Firefox")) {
      setBrowser(BrowserTypes.Firefox)
    } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
      setBrowser(BrowserTypes.Safari)
    } else {
      setBrowser("Unknown")
    }
  }

  const getOS = () => {
    const userAgent = navigator.userAgent;
    if (/windows/i.test(userAgent)) {
      setOs(OSTypes.Windows)
    } else if (/android/i.test(userAgent)) {
      setOs(OSTypes.Android)
    } else if (/iphone|ipad|ipod/i.test(userAgent)) {
      setOs(OSTypes.Ios)
    } else if (/mac/i.test(userAgent) && !/iphone|ipad|ipod/i.test(userAgent)) {
      setOs(OSTypes.Macos)
    } else {
      setOs("Unknown")
    }
  }

  const getDevice = () => {
    const userAgent = navigator.userAgent;
    if (/Mobi|Android/i.test(userAgent)) {
      setDevice(DeviceTypes.Mobile)
    } else if (/Tablet|iPad/i.test(userAgent) || (navigator.maxTouchPoints > 1 && screen.width >= 768)) {
      setDevice(DeviceTypes.Tablet)
    } else {
      setDevice(DeviceTypes.Desktop)
    }
  }

  useEffect(() => {

    getBrowser()
    getDevice()
    getOS()

    const tabActivated = typeof chrome !== 'undefined' ? chrome.tabs?.onActivated : undefined
    const tabUpdated = typeof chrome !== 'undefined' ? chrome.tabs?.onUpdated : undefined
    const tabRemoved = typeof chrome !== 'undefined' ? chrome.tabs?.onRemoved : undefined

    if (
      !hasBrowserEventListeners(tabActivated)
      || !hasBrowserEventListeners(tabUpdated)
      || !hasBrowserEventListeners(tabRemoved)
    ) {
      // Running in regular web page (Safari, Firefox, etc.)
      console.log('Chrome extension APIs not available - running in web page');
      return; // Return undefined (no cleanup needed)
    }

    let disposed = false
    let liveEditorPort: chrome.runtime.Port | null = null

    try {
      liveEditorPort = chrome.runtime.connect({ name: LIVE_EDITOR_PORT_NAME })
    } catch (error) {
      console.warn("[Prismancy] Unable to register the live editor panel.", error)
    }

    const registerLiveEditorTab = (tab: chrome.tabs.Tab | undefined) => {
      try {
        liveEditorPort?.postMessage({
          type: "live-editor-tab",
          tabId: isSupportedPageUrl(tab?.url) ? tab?.id ?? null : null,
        })
      } catch {
        // The panel or extension context is already closing.
      }
    }

    const applyTab = (tab: chrome.tabs.Tab | undefined, windowId: number | null) => {
      if (disposed) return

      activeTabIdRef.current = tab?.id ?? null
      registerLiveEditorTab(tab)
      setTabInfo((current) => ({
        tabId: tab?.id ?? null,
        tabUrl: tab?.url ?? null,
        windowId,
        isSupportedPage: isSupportedPageUrl(tab?.url),
        pageRevision: current.pageRevision + 1,
      }))
    }

    const refreshActiveTab = async (windowId: number | null = ownerWindowIdRef.current) => {
      if (windowId === null) return

      const sequence = ++refreshSequenceRef.current
      const [tab] = await chrome.tabs.query({ active: true, windowId })
      if (disposed || sequence !== refreshSequenceRef.current) return
      applyTab(tab, windowId)
    }

    const initializeCurrentWindow = async () => {
      const currentWindow = await chrome.windows.getCurrent().catch(() => null)
      if (disposed || currentWindow?.id === undefined) {
        applyTab(undefined, null)
        return
      }

      ownerWindowIdRef.current = currentWindow.id
      await refreshActiveTab(currentWindow.id)
    }

    const handleActivated = (activeInfo: chrome.tabs.TabActiveInfo) => {
      if (activeInfo.windowId !== ownerWindowIdRef.current) return

      const sequence = ++refreshSequenceRef.current
      void chrome.tabs.get(activeInfo.tabId).then((tab) => {
        if (disposed || sequence !== refreshSequenceRef.current) return
        applyTab(tab, activeInfo.windowId)
      }).catch(() => {
        if (sequence === refreshSequenceRef.current) {
          void refreshActiveTab(activeInfo.windowId)
        }
      })
    }

    const handleUpdated = (
      tabId: number,
      changeInfo: chrome.tabs.TabChangeInfo,
      tab: chrome.tabs.Tab,
    ) => {
      if (
        tab.windowId !== ownerWindowIdRef.current
        || tabId !== activeTabIdRef.current
        || (changeInfo.url === undefined && changeInfo.status !== "complete")
      ) {
        return
      }

      applyTab(tab, tab.windowId)
    }

    const handleRemoved = (tabId: number, removeInfo: chrome.tabs.TabRemoveInfo) => {
      if (removeInfo.windowId !== ownerWindowIdRef.current || tabId !== activeTabIdRef.current) {
        return
      }

      activeTabIdRef.current = null
      window.setTimeout(() => {
        void refreshActiveTab(removeInfo.windowId)
      }, 0)
    }

    void initializeCurrentWindow()

    tabActivated.addListener(handleActivated)
    tabUpdated.addListener(handleUpdated)
    tabRemoved.addListener(handleRemoved)

    return () => {
      disposed = true
      refreshSequenceRef.current += 1
      tabActivated.removeListener(handleActivated)
      tabUpdated.removeListener(handleUpdated)
      tabRemoved.removeListener(handleRemoved)
      liveEditorPort?.disconnect()
      liveEditorPort = null
    }
  }, [])

  return (
    <BrowserContext.Provider value={{ tabInfo, os, browser, device }}>
      {children}
    </BrowserContext.Provider>
  )
}

export const useBrowser = () => {
  const context = useContext(BrowserContext)
  if (!context) {
    throw new Error("useBrowser must be used within a BrowserProvider")
  }
  return context
}
